import { ReactNode } from 'react';
import { cn } from '@/lib/utils.ts';
import { Pkt } from '@/features/dashboard/tfg/types/Pkt.ts';
import {
  PKT_ORDER,
  getPktLabel,
  pktNumber,
} from '@/features/dashboard/tfg/types/Pkt.utils.ts';
import { PaymentTiming } from '@/features/dashboard/tfg/types/PaymentTiming.ts';
import {
  PAYMENT_TIMING_ORDER,
  getPaymentTimingLabel,
} from '@/features/dashboard/tfg/types/PaymentTiming.utils.ts';

export interface DeklaracjaDataColumn<T> {
  header: string;
  align?: 'left' | 'right';
  render: (row: T) => ReactNode;
}

interface DeklaracjaSectionTableProps<T> {
  title: string;
  subtitle?: string;
  rows: T[];
  getPkt: (row: T) => Pkt;
  getTiming: (row: T) => PaymentTiming;
  /**
   * Builds a zero-valued row for a pkt/timing combination missing from the data,
   * so every bucket of the official UFG form is always rendered.
   */
  makeEmptyRow: (pkt: Pkt, timing: PaymentTiming) => T;
  /**
   * Official UFG field number of the first value cell (pkt 1 / first timing /
   * first data column). Subsequent cells are numbered sequentially across
   * columns, then timing rows, then pkt buckets — e.g. 12 for Dział I, 61 for
   * Dział II.
   */
  fieldNumberBase: number;
  /** Numeric columns rendered after the fixed Pkt / description / timing columns. */
  dataColumns: DeklaracjaDataColumn<T>[];
  totalLabel: string;
  totalValue: ReactNode;
}

const alignClass = (align?: 'left' | 'right') =>
  align === 'right' ? 'text-right' : 'text-left';

/**
 * Renders one Dział of the declaration as a table that mirrors the official UFG
 * form: rows grouped by pkt bucket (PKT_1→PKT_6), each with its payment-timing
 * sub-rows, and a section total in the last column.
 */
const DeklaracjaSectionTable = <T,>({
  title,
  subtitle,
  rows,
  getPkt,
  getTiming,
  makeEmptyRow,
  fieldNumberBase,
  dataColumns,
  totalLabel,
  totalValue,
}: DeklaracjaSectionTableProps<T>) => {
  const groups = PKT_ORDER.map((pkt, pktIndex) => ({
    pkt,
    pktIndex,
    rows: PAYMENT_TIMING_ORDER.map(
      (timing) =>
        rows.find((row) => getPkt(row) === pkt && getTiming(row) === timing) ??
        makeEmptyRow(pkt, timing),
    ),
  }));

  /** Cells per pkt bucket = timing rows × data columns. */
  const cellsPerPkt = PAYMENT_TIMING_ORDER.length * dataColumns.length;

  const totalLabelColSpan = 2 + dataColumns.length;

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-base-text">{title}</h3>
        {subtitle && <p className="text-sm text-neutral-500">{subtitle}</p>}
      </div>
      <div className="overflow-x-auto rounded-lg border border-neutral-200">
        <table className="w-full table-fixed border-collapse text-sm">
          <thead>
            <tr className="border-b border-neutral-200 bg-neutral-50 text-left text-xs font-medium uppercase tracking-wide text-neutral-500">
              <th className="w-[5%] px-3 py-2">Pkt</th>
              <th className="w-[26%] px-3 py-2 align-top">
                Charakter wykonywanej działalności, rodzaj usługi, miejsce
                realizacji imprezy turystycznej, sposób transportu i rodzaj
                zapewnianego środka transportu
              </th>
              <th className="w-[19%] px-3 py-2 align-top">Termin wpłaty</th>
              {dataColumns.map((column) => (
                <th
                  key={column.header}
                  className={cn(
                    'px-3 py-2 align-top',
                    alignClass(column.align),
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.flatMap((group) =>
              group.rows.map((row, index) => (
                <tr
                  key={`${group.pkt}-${getTiming(row)}`}
                  className="border-b border-neutral-100 align-top"
                >
                  {index === 0 && (
                    <td
                      rowSpan={group.rows.length}
                      className="px-3 py-2 font-medium text-base-text"
                    >
                      {pktNumber[group.pkt]}
                    </td>
                  )}
                  {index === 0 && (
                    <td
                      rowSpan={group.rows.length}
                      className="px-3 py-2 text-neutral-600"
                    >
                      {getPktLabel(group.pkt)}
                    </td>
                  )}
                  <td className="px-3 py-2 text-neutral-600">
                    {getPaymentTimingLabel(getTiming(row))}
                  </td>
                  {dataColumns.map((column, columnIndex) => (
                    <td
                      key={column.header}
                      className={cn(
                        'relative px-3 pb-2 pt-5 tabular-nums text-base-text',
                        alignClass(column.align),
                      )}
                    >
                      <span className="absolute left-1.5 top-1 text-[10px] font-normal leading-none text-neutral-400">
                        {fieldNumberBase +
                          group.pktIndex * cellsPerPkt +
                          index * dataColumns.length +
                          columnIndex}
                      </span>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              )),
            )}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-neutral-300 bg-neutral-50 font-semibold text-base-text">
              <td colSpan={totalLabelColSpan} className="px-3 py-2 text-right">
                {totalLabel}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                {totalValue}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

export default DeklaracjaSectionTable;
